import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { calculateLinearMonthlyDepreciation, findDepreciationRuleForAsset, formatCurrency } from "../domain/depreciation";
import type { Asset, Company, DepreciationRule } from "../types/imobi";

type AssetWithPhotos = Asset & {
  photoUrls?: string[];
};

type PdfImage = {
  dataUrl: string;
  format: "PNG" | "JPEG";
};

const IMAGE_LOAD_TIMEOUT_MS = 6000;

export type AssetReportField =
  | "company"
  | "patrimonyNumber"
  | "internalCode"
  | "description"
  | "assetTypeId"
  | "physicalLocation"
  | "department"
  | "responsible"
  | "status"
  | "conservationState"
  | "brand"
  | "model"
  | "serialNumber"
  | "vehiclePlate"
  | "renavam"
  | "chassis"
  | "manufactureYear"
  | "modelYear"
  | "mileageKm"
  | "builtAreaM2"
  | "dimensions"
  | "purchaseDate"
  | "assetAge"
  | "acquisitionValue"
  | "currentEstimatedValue"
  | "auditedValue"
  | "bookValue"
  | "auditStatus"
  | "inactiveReason"
  | "maintenanceStartedAt"
  | "maintenanceReason"
  | "photosCount"
  | "mainPhotoUrl"
  | "customFields";

export const assetReportFields: Array<{ id: AssetReportField; label: string }> = [
  { id: "company", label: "Empresa" },
  { id: "patrimonyNumber", label: "Patrimônio" },
  { id: "internalCode", label: "Codigo interno" },
  { id: "description", label: "Descrição" },
  { id: "assetTypeId", label: "Tipo" },
  { id: "physicalLocation", label: "Local" },
  { id: "department", label: "Departamento" },
  { id: "responsible", label: "Responsável" },
  { id: "status", label: "Status" },
  { id: "conservationState", label: "Conservação" },
  { id: "brand", label: "Marca" },
  { id: "model", label: "Modelo" },
  { id: "serialNumber", label: "Número de série" },
  { id: "vehiclePlate", label: "Placa" },
  { id: "renavam", label: "Renavam" },
  { id: "chassis", label: "Chassi" },
  { id: "manufactureYear", label: "Ano fabricação" },
  { id: "modelYear", label: "Ano modelo" },
  { id: "mileageKm", label: "Quilometragem" },
  { id: "builtAreaM2", label: "Área m²" },
  { id: "dimensions", label: "Dimensões" },
  { id: "purchaseDate", label: "Data de aquisição" },
  { id: "assetAge", label: "Idade do bem" },
  { id: "acquisitionValue", label: "Valor de aquisição" },
  { id: "currentEstimatedValue", label: "Valor estimado atual" },
  { id: "auditedValue", label: "Valor auditado" },
  { id: "bookValue", label: "Valor contábil" },
  { id: "auditStatus", label: "Auditoria" },
  { id: "inactiveReason", label: "Motivo de inativação" },
  { id: "maintenanceStartedAt", label: "Entrada em manutenção" },
  { id: "maintenanceReason", label: "Motivo da manutenção" },
  { id: "photosCount", label: "Quantidade de fotos" },
  { id: "mainPhotoUrl", label: "Foto principal" },
  { id: "customFields", label: "Campos dinâmicos" }
];

export type AssetReportGroupBy = "none" | "assetTypeId" | "responsible" | "physicalLocation";

function dataUrlFormat(dataUrl: string): "PNG" | "JPEG" | null {
  if (dataUrl.startsWith("data:image/png")) {
    return "PNG";
  }

  if (dataUrl.startsWith("data:image/jpeg") || dataUrl.startsWith("data:image/jpg")) {
    return "JPEG";
  }

  return null;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs = IMAGE_LOAD_TIMEOUT_MS): Promise<T | null> {
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => resolve(null), timeoutMs);
    promise
      .then((result) => resolve(result))
      .catch(() => resolve(null))
      .finally(() => window.clearTimeout(timeout));
  });
}

function readBlobAsDataUrl(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

async function normalizeImageDataUrl(dataUrl: string, preserveTransparency: boolean): Promise<PdfImage | null> {
  const directFormat = dataUrlFormat(dataUrl);
  if (preserveTransparency && directFormat) {
    return { dataUrl, format: directFormat };
  }

  return new Promise((resolve) => {
    const image = new window.Image();
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const context = canvas.getContext("2d");
        if (!context || !canvas.width || !canvas.height) {
          resolve(null);
          return;
        }

        if (!preserveTransparency) {
          context.fillStyle = "#ffffff";
          context.fillRect(0, 0, canvas.width, canvas.height);
        }

        context.drawImage(image, 0, 0);
        const output = preserveTransparency ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", 0.82);
        resolve({ dataUrl: output, format: preserveTransparency ? "PNG" : "JPEG" });
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
}

async function loadImageFromUrlCanvas(url: string, preserveTransparency: boolean): Promise<PdfImage | null> {
  return new Promise((resolve) => {
    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const context = canvas.getContext("2d");
        if (!context || !canvas.width || !canvas.height) {
          resolve(null);
          return;
        }
        if (!preserveTransparency) {
          context.fillStyle = "#ffffff";
          context.fillRect(0, 0, canvas.width, canvas.height);
        }

        context.drawImage(image, 0, 0);
        const output = preserveTransparency ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", 0.82);
        resolve({ dataUrl: output, format: preserveTransparency ? "PNG" : "JPEG" });
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

async function loadPdfImage(url: string, preserveTransparency = false): Promise<PdfImage | null> {
  try {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), IMAGE_LOAD_TIMEOUT_MS);
    const response = await fetch(url, { mode: "cors", cache: "force-cache", signal: controller.signal });
    window.clearTimeout(timeout);
    if (!response.ok) {
      return withTimeout(loadImageFromUrlCanvas(url, preserveTransparency));
    }

    const dataUrl = await readBlobAsDataUrl(await response.blob());
    if (dataUrl) {
      const normalized = await normalizeImageDataUrl(dataUrl, preserveTransparency);
      if (normalized) {
        return normalized;
      }
    }

    return withTimeout(loadImageFromUrlCanvas(url, preserveTransparency));
  } catch {
    return withTimeout(loadImageFromUrlCanvas(url, preserveTransparency));
  }
}

function getFieldValue(field: AssetReportField, asset: Asset, companiesById: Map<string, Company>, depreciationRules: DepreciationRule[] = []): string {
  const depreciation = calculateLinearMonthlyDepreciation(asset, findDepreciationRuleForAsset(asset, depreciationRules));
  const company = companiesById.get(asset.companyId);
  const values: Record<AssetReportField, string> = {
    company: company?.tradeName ?? asset.companyId,
    patrimonyNumber: asset.patrimonyNumber,
    internalCode: asset.internalCode,
    description: asset.description,
    assetTypeId: asset.assetTypeId,
    physicalLocation: asset.physicalLocation,
    department: asset.department ?? "",
    responsible: asset.responsible ?? "",
    status: asset.status,
    conservationState: asset.conservationState,
    brand: asset.brand ?? "",
    model: asset.model ?? "",
    serialNumber: asset.serialNumber ?? "",
    vehiclePlate: asset.vehiclePlate ?? "",
    renavam: asset.renavam ?? "",
    chassis: asset.chassis ?? "",
    manufactureYear: asset.manufactureYear ? String(asset.manufactureYear) : "",
    modelYear: asset.modelYear ? String(asset.modelYear) : "",
    mileageKm: asset.mileageKm ? `${asset.mileageKm} km` : "",
    builtAreaM2: asset.builtAreaM2 ? String(asset.builtAreaM2) : "",
    dimensions: asset.dimensions ?? "",
    purchaseDate: formatDateOnly(asset.purchaseDate),
    assetAge: formatAssetAge(asset.purchaseDate),
    acquisitionValue: formatCurrency(asset.acquisitionValue ?? 0),
    currentEstimatedValue: asset.currentEstimatedValue ? formatCurrency(asset.currentEstimatedValue) : "",
    auditedValue: asset.auditedValue ? formatCurrency(asset.auditedValue) : "",
    bookValue: formatCurrency(depreciation.currentBookValue),
    auditStatus: formatAuditStatus(asset.auditStatus),
    inactiveReason: asset.inactiveReason ?? "",
    maintenanceStartedAt: formatDateOnly(asset.maintenanceStartedAt),
    maintenanceReason: asset.maintenanceReason ?? "",
    photosCount: String(asset.photosCount ?? 0),
    mainPhotoUrl: asset.mainPhotoUrl ?? "",
    customFields: asset.customFields ? JSON.stringify(asset.customFields) : ""
  };

  return values[field];
}

function getGroupLabel(groupBy: AssetReportGroupBy, asset: Asset): string {
  if (groupBy === "assetTypeId") {
    return asset.assetTypeId || "Sem tipo";
  }
  if (groupBy === "responsible") {
    return asset.responsible || "Sem responsável";
  }
  if (groupBy === "physicalLocation") {
    return asset.physicalLocation || "Sem local";
  }
  return "";
}

function groupedAssets(assets: AssetWithPhotos[], groupBy: AssetReportGroupBy): Array<{ label: string; assets: AssetWithPhotos[] }> {
  if (groupBy === "none") {
    return [{ label: "", assets }];
  }

  const groups = new Map<string, AssetWithPhotos[]>();
  assets.forEach((asset) => {
    const label = getGroupLabel(groupBy, asset);
    groups.set(label, [...(groups.get(label) ?? []), asset]);
  });
  return Array.from(groups.entries())
    .sort(([left], [right]) => left.localeCompare(right, "pt-BR"))
    .map(([label, groupAssets]) => ({ label, assets: groupAssets }));
}

function calculateReportTotals(assets: Asset[], depreciationRules: DepreciationRule[]) {
  return assets.reduce((totals, asset) => {
    const depreciation = calculateLinearMonthlyDepreciation(asset, findDepreciationRuleForAsset(asset, depreciationRules));
    return {
      acquisition: totals.acquisition + (asset.acquisitionValue ?? 0),
      book: totals.book + depreciation.currentBookValue,
      accumulated: totals.accumulated + depreciation.accumulatedDepreciation
    };
  }, { acquisition: 0, book: 0, accumulated: 0 });
}

function formatDateOnly(value?: string): string {
  if (!value) {
    return "-";
  }

  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(new Date(`${value}T00:00:00`));
}

function formatAssetAge(value?: string): string {
  if (!value) {
    return "-";
  }

  const start = new Date(`${value}T00:00:00`);
  const today = new Date();
  if (Number.isNaN(start.getTime()) || start > today) {
    return "-";
  }

  let years = today.getFullYear() - start.getFullYear();
  let months = today.getMonth() - start.getMonth();
  if (today.getDate() < start.getDate()) {
    months -= 1;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const parts = [];
  if (years > 0) {
    parts.push(`${years} ano${years === 1 ? "" : "s"}`);
  }
  parts.push(`${months} ${months === 1 ? "mês" : "meses"}`);
  return parts.join(" e ");
}

function formatAuditStatus(status?: Asset["auditStatus"]): string {
  const labels: Record<NonNullable<Asset["auditStatus"]>, string> = {
    auditado: "Auditado",
    divergente: "Divergente",
    nao_auditado: "Não auditado",
    pendente_revisao: "Pendente de revisão"
  };

  return labels[status ?? "nao_auditado"];
}

function escapeCsv(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function addFooter(doc: jsPDF, version: string, generatedAt: string, generatedBy: string) {
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(226, 232, 240);
    doc.line(14, 197, 283, 197);
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text(`IMOBI v${version} | Gerado em ${generatedAt} | Usuário: ${generatedBy}`, 14, 202);
    doc.text(`Página ${page} de ${totalPages}`, 252, 202);
  }
  doc.setTextColor(0, 0, 0);
}

function photoUrls(asset: AssetWithPhotos): string[] {
  const urls = asset.photoUrls?.length ? asset.photoUrls : asset.mainPhotoUrl ? [asset.mainPhotoUrl] : [];
  return Array.from(new Set(urls.filter(Boolean))).slice(0, 4);
}

export function exportAssetsCsv(companies: Company[], assets: Asset[], fields: AssetReportField[], depreciationRules: DepreciationRule[] = []): void {
  const companiesById = new Map(companies.map((company) => [company.id, company]));
  const headers = fields.map((field) => assetReportFields.find((item) => item.id === field)?.label ?? field);
  const rows = assets.map((asset) => fields.map((field) => getFieldValue(field, asset, companiesById, depreciationRules)));
  const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(";")).join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `imobi-bens-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function exportAssetsPdf({
  companies,
  assets,
  includePhotos,
  imobiLogoUrl,
  generatedBy,
  depreciationRules = [],
  groupBy = "none",
  includeTotals = true,
  version = "0.1.70"
}: {
  companies: Company[];
  assets: AssetWithPhotos[];
  includePhotos: boolean;
  imobiLogoUrl: string;
  generatedBy: string;
  depreciationRules?: DepreciationRule[];
  groupBy?: AssetReportGroupBy;
  includeTotals?: boolean;
  version?: string;
}): Promise<void> {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const generatedAt = new Date().toLocaleString("pt-BR");
  const companiesById = new Map(companies.map((company) => [company.id, company]));
  const imobiLogo = await loadPdfImage(imobiLogoUrl, true);
  const companyLogos = await Promise.all(companies.slice(0, 4).map(async (company) => ({
    company,
    logo: company.logoUrl ? await loadPdfImage(company.logoUrl, true) : null
  })));

  doc.setFontSize(16);
  doc.text("Relatório de Bens Imobilizados", 14, 16);
  doc.setFontSize(9);
  doc.text(`${companies.length} empresa(s) selecionada(s) - ${assets.length} bem(ns)`, 14, 23);

  if (imobiLogo) {
    doc.addImage(imobiLogo.dataUrl, imobiLogo.format, 232, 8, 24, 16, undefined, "FAST");
  }

  if (companies.length === 1 && companyLogos[0]?.logo) {
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(262, 7, 21, 17, 2, 2);
    doc.addImage(companyLogos[0].logo.dataUrl, companyLogos[0].logo.format, 264, 9, 17, 13, undefined, "FAST");
  } else {
    companyLogos.forEach((item, index) => {
      if (item.logo) {
        doc.addImage(item.logo.dataUrl, item.logo.format, 262 + (index * 8), 9, 7, 7, undefined, "FAST");
      }
    });
  }

  doc.setFontSize(8);
  companies.slice(0, 3).forEach((company, index) => {
    doc.text(`${company.tradeName} - ${company.cnpj}`, 14, 30 + (index * 4));
  });
  if (companies.length > 3) {
    doc.text(`+ ${companies.length - 3} empresa(s)`, 14, 42);
  }

  if (includeTotals) {
    const totals = calculateReportTotals(assets, depreciationRules);
    doc.setFontSize(8);
    doc.setTextColor(15, 23, 42);
    doc.text(`Total aquisição: ${formatCurrency(totals.acquisition)}`, 128, 30);
    doc.text(`Valor desvalorizado: ${formatCurrency(totals.book)}`, 128, 35);
    doc.text(`Desvalorização acumulada: ${formatCurrency(totals.accumulated)}`, 128, 40);
  }

  if (includePhotos) {
    await renderAssetPhotoReport(doc, assets, companiesById, depreciationRules, groupBy, includeTotals);
  } else {
    const tableRows: Array<Record<string, string | number> | string[]> = [];
    groupedAssets(assets, groupBy).forEach((group) => {
      if (group.label) {
        tableRows.push([{ content: group.label, colSpan: 11, styles: { fillColor: [240, 253, 250], textColor: [15, 118, 110], fontStyle: "bold" } } as unknown as string]);
      }
      group.assets.forEach((asset) => {
        const depreciation = calculateLinearMonthlyDepreciation(asset, findDepreciationRuleForAsset(asset, depreciationRules));
        const company = companiesById.get(asset.companyId);
        tableRows.push([
          company?.tradeName ?? asset.companyId,
          asset.patrimonyNumber,
          asset.description,
          asset.physicalLocation,
          asset.responsible ?? "-",
          asset.status,
          formatDateOnly(asset.purchaseDate),
          formatAssetAge(asset.purchaseDate),
          formatCurrency(asset.acquisitionValue ?? 0),
          formatCurrency(depreciation.currentBookValue),
          formatAuditStatus(asset.auditStatus)
        ]);
      });
    });
    autoTable(doc, {
      startY: companies.length > 3 ? 48 : 44,
      margin: { bottom: 16 },
      head: [["Empresa", "Patrimônio", "Descrição", "Local", "Responsável", "Status", "Data aquisição", "Idade", "Aquisição", "Valor contábil", "Auditoria"]],
      body: tableRows,
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: { 2: { cellWidth: 54 } },
      headStyles: { fillColor: [15, 118, 110] }
    });
  }

  addFooter(doc, version, generatedAt, generatedBy);
  doc.save(`imobi-relatorio-bens-${new Date().toISOString().slice(0, 10)}.pdf`);
}

async function renderAssetPhotoReport(doc: jsPDF, assets: AssetWithPhotos[], companiesById: Map<string, Company>, depreciationRules: DepreciationRule[], groupBy: AssetReportGroupBy, includeTotals: boolean) {
  let y = 44;
  const rowHeight = 28;
  const rowGap = 2;

  for (const group of groupedAssets(assets, groupBy)) {
    if (group.label) {
      if (y + 8 > 194) {
        doc.addPage("a4", "landscape");
        y = 18;
      }
      doc.setFillColor(240, 253, 250);
      doc.roundedRect(14, y, 269, 6, 1, 1, "F");
      doc.setFontSize(8);
      doc.setTextColor(15, 118, 110);
      doc.text(group.label, 17, y + 4);
      y += 8;
    }

  for (const asset of group.assets) {
    if (y + rowHeight > 194) {
      doc.addPage("a4", "landscape");
      y = 18;
    }

    const depreciation = calculateLinearMonthlyDepreciation(asset, findDepreciationRuleForAsset(asset, depreciationRules));
    const company = companiesById.get(asset.companyId);
    const urls = photoUrls(asset);

    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(14, y, 269, rowHeight, 2, 2);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(14, y, 30, rowHeight, 2, 2, "F");

    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(asset.patrimonyNumber, 18, y + 9, { maxWidth: 22 });
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text(company?.tradeName ?? asset.companyId, 18, y + 18, { maxWidth: 22 });

    doc.setFontSize(7);
    doc.setTextColor(15, 23, 42);
    doc.text("Descrição", 48, y + 6);
    doc.setTextColor(71, 85, 105);
    doc.text(asset.description || "-", 48, y + 11, { maxWidth: 55 });
    doc.setTextColor(15, 23, 42);
    doc.text("Marca", 48, y + 17);
    doc.setTextColor(71, 85, 105);
    doc.text(asset.brand || asset.model || "-", 64, y + 17, { maxWidth: 39 });
    doc.setTextColor(15, 23, 42);
    doc.text(asset.builtAreaM2 ? "Endereço / m²" : "Local", 48, y + 22);
    doc.setTextColor(71, 85, 105);
    doc.text(asset.builtAreaM2 ? `${asset.physicalLocation || "-"} | ${asset.builtAreaM2} m²` : asset.physicalLocation || "-", 48, y + 26, { maxWidth: 55 });

    for (let index = 0; index < 4; index += 1) {
      const x = 112 + (index * 24);
      doc.setDrawColor(203, 213, 225);
      doc.roundedRect(x, y + 4, 20, 20, 1.5, 1.5);
      const image = urls[index] ? await loadPdfImage(urls[index], false) : null;
      if (image) {
        doc.addImage(image.dataUrl, image.format, x + 1, y + 5, 18, 18, undefined, "FAST");
      } else {
        doc.setFontSize(7);
        doc.setTextColor(148, 163, 184);
        doc.text("Foto", x + 6, y + 16);
      }
    }

    doc.setFontSize(7);
    doc.setTextColor(15, 23, 42);
    doc.text("Valor", 214, y + 6);
    doc.setTextColor(71, 85, 105);
    doc.text(formatCurrency(asset.acquisitionValue ?? 0), 236, y + 6);
    doc.setTextColor(15, 23, 42);
    doc.text("Contábil", 214, y + 12);
    doc.setTextColor(71, 85, 105);
    doc.text(formatCurrency(depreciation.currentBookValue), 236, y + 12);
    doc.setTextColor(15, 23, 42);
    doc.text("Auditado", 214, y + 18);
    doc.setTextColor(71, 85, 105);
    doc.text(formatAuditStatus(asset.auditStatus), 236, y + 18, { maxWidth: 28 });
    doc.setTextColor(15, 23, 42);
    doc.text("Idade", 214, y + 24);
    doc.setTextColor(71, 85, 105);
    doc.text(formatAssetAge(asset.purchaseDate), 236, y + 24, { maxWidth: 28 });
    doc.setTextColor(15, 23, 42);
    doc.text("Conservação", 252, y + 7);
    doc.setTextColor(71, 85, 105);
    doc.text(asset.conservationState, 252, y + 14);

    y += rowHeight + rowGap;
  }
  }

  if (includeTotals) {
    if (y + 14 > 194) {
      doc.addPage("a4", "landscape");
      y = 18;
    }
    const totals = calculateReportTotals(assets, depreciationRules);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(14, y, 269, 12, 2, 2, "F");
    doc.setFontSize(8);
    doc.setTextColor(15, 23, 42);
    doc.text(`Totais: aquisição ${formatCurrency(totals.acquisition)} | valor desvalorizado ${formatCurrency(totals.book)} | desvalorização acumulada ${formatCurrency(totals.accumulated)}`, 18, y + 7);
  }

  doc.setTextColor(0, 0, 0);
}
